import type { FileMap } from "../stores/fileStore/types";
import type { MjcfUploadDependency } from "./mjcfUploadDependencies";

export type XacroExpansionResult = {
  urdfFile: File;
  warnings: string[];
  referencedFiles: string[];
};

const ELEMENT_NODE = 1;

type XacroContext = {
  fileMap: FileMap;
  packageRoots: Map<string, string>;
  macros: Map<string, XacroMacroDefinition>;
  macroStack: Set<string>;
  warnings: string[];
  referencedFiles: Set<string>;
  visited: Set<string>;
};

type XacroMacroParam = {
  name: string;
  defaultValue: string | null;
};

type XacroMacroDefinition = {
  name: string;
  params: XacroMacroParam[];
  nodes: Node[];
};

const normalizeUploadPath = (value: string | undefined | null): string =>
  String(value || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "");

const dirname = (path: string): string => {
  const normalized = normalizeUploadPath(path);
  const index = normalized.lastIndexOf("/");
  return index >= 0 ? normalized.slice(0, index) : "";
};

const joinPath = (base: string, path: string): string => {
  const normalizedPath = normalizeUploadPath(path);
  if (!normalizedPath || normalizedPath.includes("://")) return normalizedPath;
  if (!base) return normalizedPath;
  return normalizeUploadPath(`${base}/${normalizedPath}`);
};

const basename = (path: string): string => {
  const normalized = normalizeUploadPath(path);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
};

const isSameFile = (a: File | null | undefined, b: File | null | undefined): boolean => {
  if (!a || !b) return false;
  return a === b || (a.name === b.name && a.size === b.size && a.lastModified === b.lastModified);
};

const getFileMapKeyForFile = (file: File, fileMap: FileMap): string => {
  const relativePath = normalizeUploadPath(
    (file as File & { webkitRelativePath?: string }).webkitRelativePath
  );
  if (relativePath) return relativePath;
  const exactEntries = Object.entries(fileMap || {}).filter(([, candidate]) =>
    isSameFile(candidate, file)
  );
  const pathEntry = exactEntries.find(([key]) => normalizeUploadPath(key).includes("/"));
  return normalizeUploadPath(pathEntry?.[0] || exactEntries[0]?.[0] || file.name);
};

const fileIdentityKey = (file: File, fileKey: string): string =>
  `${normalizeUploadPath(fileKey).toLowerCase()}:${file.name}:${file.size}:${file.lastModified}`;

const parseXml = async (file: File, operation: string): Promise<Document> => {
  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, "application/xml");
  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    throw new Error(`XML parsing failed while ${operation} ${file.name}`);
  }
  return doc;
};

const createEmptyXacroDocument = (): Document =>
  new DOMParser().parseFromString(
    `<robot xmlns:xacro="http://www.ros.org/wiki/xacro"/>`,
    "application/xml"
  );

const getXacroLocalName = (element: Element): string =>
  (element.localName || element.tagName.replace(/^xacro:/i, "")).toLowerCase();

const isXacroElement = (element: Element, localName?: string): boolean => {
  const tagName = element.tagName.toLowerCase();
  const prefix = element.prefix?.toLowerCase() || "";
  const isXacro = prefix === "xacro" || tagName.startsWith("xacro:");
  if (!isXacro) return false;
  return localName ? getXacroLocalName(element) === localName.toLowerCase() : true;
};

const resolveFileFromMap = (
  requestedPath: string,
  currentFileKey: string,
  fileMap: FileMap
): { file: File; key: string } | null => {
  const normalizedRequested = normalizeUploadPath(requestedPath);
  if (!normalizedRequested) return null;

  const currentDir = dirname(currentFileKey);
  const lookupPaths = Array.from(
    new Set([joinPath(currentDir, normalizedRequested), normalizedRequested].filter(Boolean))
  );
  const entries = Object.entries(fileMap || {});
  const exact = (path: string) => {
    const lowerPath = normalizeUploadPath(path).toLowerCase();
    const match = entries.find(([key]) => normalizeUploadPath(key).toLowerCase() === lowerPath);
    return match ? { key: normalizeUploadPath(match[0]), file: match[1] } : null;
  };

  for (const path of lookupPaths) {
    const match = exact(path);
    if (match?.file) return match;
  }

  for (const path of lookupPaths) {
    const lowerPath = normalizeUploadPath(path).toLowerCase();
    const suffixMatches = entries.filter(([key]) =>
      normalizeUploadPath(key).toLowerCase().endsWith(`/${lowerPath}`)
    );
    const uniqueFiles = Array.from(new Set(suffixMatches.map(([, file]) => file).filter(Boolean)));
    if (uniqueFiles.length === 1) {
      const [key, file] = suffixMatches.find(([, candidate]) => candidate === uniqueFiles[0]) || [];
      if (key && file) return { key: normalizeUploadPath(key), file };
    }
  }

  const requestedBasename = basename(normalizedRequested).toLowerCase();
  const basenameMatches = entries.filter(
    ([key]) => basename(key).toLowerCase() === requestedBasename
  );
  const uniqueFiles = Array.from(new Set(basenameMatches.map(([, file]) => file).filter(Boolean)));
  if (uniqueFiles.length === 1) {
    const [key, file] = basenameMatches.find(([, candidate]) => candidate === uniqueFiles[0]) || [];
    if (key && file) return { key: normalizeUploadPath(key), file };
  }

  return null;
};

const readPackageName = async (file: File): Promise<string> => {
  try {
    const doc = await parseXml(file, "reading package metadata from");
    return doc.querySelector("package > name")?.textContent?.trim() || "";
  } catch {
    return "";
  }
};

const buildPackageRoots = async (fileMap: FileMap): Promise<Map<string, string>> => {
  const roots = new Map<string, string>();
  const entries = Object.entries(fileMap || {});

  for (const [rawKey, file] of entries) {
    const key = normalizeUploadPath(rawKey);
    if (!file || basename(key).toLowerCase() !== "package.xml") continue;
    const packageRoot = dirname(key);
    const packageName = await readPackageName(file);
    if (packageName && !roots.has(packageName)) roots.set(packageName, packageRoot);
    const folderName = basename(packageRoot);
    if (folderName && !roots.has(folderName)) roots.set(folderName, packageRoot);
  }

  for (const [rawKey] of entries) {
    const key = normalizeUploadPath(rawKey);
    const [firstSegment] = key.split("/");
    if (!firstSegment || firstSegment === basename(key)) continue;
    if (!roots.has(firstSegment)) roots.set(firstSegment, firstSegment);
  }

  return roots;
};

const formatXacroNumber = (value: number): string => {
  if (!Number.isFinite(value)) return "";
  if (Math.abs(value) < 1e-15) return "0";
  return Number(value.toPrecision(15)).toString();
};

const evaluateSimpleXacroExpression = (expression: string): string | null => {
  const trimmed = expression.trim();
  const functionMatch = trimmed.match(/^(radians|degrees)\(\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*\)$/i);
  if (!functionMatch) return null;
  const value = Number(functionMatch[2]);
  if (!Number.isFinite(value)) return null;
  const functionName = functionMatch[1].toLowerCase();
  if (functionName === "radians") return formatXacroNumber((value * Math.PI) / 180);
  return formatXacroNumber((value * 180) / Math.PI);
};

const substituteText = (
  value: string,
  variables: Map<string, string>,
  context: XacroContext
): string =>
  value
    .replace(/\$\(([^)]+)\)/g, (_match, expression: string) => {
      const [command, ...rest] = expression.trim().split(/\s+/);
      const argument = rest.join(" ").trim();
      if (command === "arg") {
        if (variables.has(argument)) return variables.get(argument) || "";
        context.warnings.push(`[Xacro] Argument "${argument}" is not defined.`);
        return "";
      }
      if (command === "find") {
        const root = context.packageRoots.get(argument);
        if (root) return root;
        context.warnings.push(
          `[Xacro] Package "${argument}" from "$(find ${argument})" was not found in uploaded files.`
        );
        return "";
      }
      context.warnings.push(
        `[Xacro] Substitution "$(${expression.trim()})" is not supported in browser imports.`
      );
      return "";
    })
    .replace(/\$\{([^}]+)\}/g, (_match, rawName: string) => {
      const name = rawName.trim();
      if (variables.has(name)) return variables.get(name) || "";
      const evaluated = evaluateSimpleXacroExpression(name);
      if (evaluated !== null) return evaluated;
      context.warnings.push(`[Xacro] Property "${name}" is not defined.`);
      return "";
    });

const substituteAttributes = (
  element: Element,
  variables: Map<string, string>,
  context: XacroContext
): void => {
  Array.from(element.attributes).forEach((attribute) => {
    const nextValue = substituteText(attribute.value, variables, context);
    if (nextValue !== attribute.value) element.setAttribute(attribute.name, nextValue);
  });
};

const parseMacroParams = (rawParams: string, macroName: string, context: XacroContext) =>
  rawParams
    .split(/\s+/)
    .map((param) => param.trim())
    .filter(Boolean)
    .map((param): XacroMacroParam | null => {
      if (param.startsWith("*")) {
        context.warnings.push(
          `[Xacro] Macro "${macroName}" uses block parameter "${param}", which is not supported yet.`
        );
        return null;
      }
      const defaultMatch = param.match(/^([^:=]+)(?::=|=)(.*)$/);
      if (defaultMatch) {
        return {
          name: defaultMatch[1].trim(),
          defaultValue: defaultMatch[2] ?? "",
        };
      }
      return { name: param, defaultValue: null };
    })
    .filter((param): param is XacroMacroParam => !!param && !!param.name);

const collectXacroDefinitions = (
  root: Element,
  variables: Map<string, string>,
  context: XacroContext
): void => {
  const visit = (element: Element): void => {
    if (isXacroElement(element, "arg")) {
      const name = element.getAttribute("name")?.trim() || "";
      const value = substituteText(
        element.getAttribute("default") || element.getAttribute("value") || "",
        variables,
        context
      );
      if (name && !variables.has(name)) variables.set(name, value);
      element.remove();
      return;
    }

    if (isXacroElement(element, "property")) {
      const name = element.getAttribute("name")?.trim() || "";
      const value = substituteText(element.getAttribute("value") || "", variables, context);
      if (name) variables.set(name, value);
      element.remove();
      return;
    }

    if (isXacroElement(element, "macro")) {
      const name = element.getAttribute("name")?.trim() || "";
      if (!name) {
        context.warnings.push("[Xacro] Unnamed macro was ignored.");
        element.remove();
        return;
      }
      context.macros.set(name, {
        name,
        params: parseMacroParams(element.getAttribute("params") || "", name, context),
        nodes: Array.from(element.childNodes).map((node) => node.cloneNode(true)),
      });
      element.remove();
      return;
    }

    Array.from(element.children).forEach(visit);
  };

  Array.from(root.children).forEach(visit);
};

const expandMacroInvocation = (
  element: Element,
  variables: Map<string, string>,
  context: XacroContext
): boolean => {
  if (!isXacroElement(element)) return false;
  const macroName = getXacroLocalName(element);
  const macro = context.macros.get(macroName);
  if (!macro) return false;

  if (context.macroStack.has(macroName)) {
    context.warnings.push(`[Xacro] Recursive macro "${macroName}" was ignored.`);
    element.remove();
    return true;
  }

  const localVariables = new Map(variables);
  macro.params.forEach((param) => {
    const rawValue = element.getAttribute(param.name);
    if (rawValue !== null) {
      localVariables.set(param.name, substituteText(rawValue, variables, context));
      return;
    }
    if (param.defaultValue !== null) {
      localVariables.set(param.name, substituteText(param.defaultValue, variables, context));
      return;
    }
    context.warnings.push(
      `[Xacro] Macro "${macroName}" parameter "${param.name}" was not provided.`
    );
    localVariables.set(param.name, "");
  });

  context.macroStack.add(macroName);
  const importedNodes = macro.nodes.map((node) => element.ownerDocument.importNode(node, true));
  element.replaceWith(...importedNodes);
  importedNodes.forEach((node) => {
    if (node.nodeType === ELEMENT_NODE) {
      processXacroNode(node as Element, localVariables, context);
    }
  });
  context.macroStack.delete(macroName);
  return true;
};

const processXacroNode = (
  element: Element,
  variables: Map<string, string>,
  context: XacroContext
): void => {
  if (expandMacroInvocation(element, variables, context)) return;

  if (isXacroElement(element)) {
    context.warnings.push(
      `[Xacro] Element <${element.tagName}> is not supported yet and was ignored.`
    );
    element.remove();
    return;
  }

  substituteAttributes(element, variables, context);
  Array.from(element.children).forEach((child) => processXacroNode(child, variables, context));
};

const processXacroDefinitions = (
  root: Element,
  variables: Map<string, string>,
  context: XacroContext
): void => {
  collectXacroDefinitions(root, variables, context);
  processXacroNode(root, variables, context);
};

const expandIncludes = async (
  doc: Document,
  fileKey: string,
  variables: Map<string, string>,
  context: XacroContext
): Promise<void> => {
  const root = doc.documentElement;
  if (!root) return;
  const includeElements: Element[] = [];
  const collectIncludes = (element: Element): void => {
    Array.from(element.children).forEach((child) => {
      if (isXacroElement(child, "include")) includeElements.push(child);
      collectIncludes(child);
    });
  };
  collectIncludes(root);

  for (const includeElement of includeElements) {
    const rawIncludePath =
      includeElement.getAttribute("filename") || includeElement.getAttribute("file") || "";
    const includePath = substituteText(rawIncludePath, variables, context);
    const resolved = resolveFileFromMap(includePath, fileKey, context.fileMap);
    if (!resolved) {
      context.warnings.push(
        `[Xacro] Include "${includePath || rawIncludePath}" could not be resolved from uploaded files.`
      );
      includeElement.remove();
      continue;
    }

    context.referencedFiles.add(resolved.key);
    const includeDoc = await expandXacroDocument(resolved.file, resolved.key, variables, context);
    const includeRoot = includeDoc.documentElement;
    const sourceNodes =
      includeRoot?.tagName?.toLowerCase() === "robot"
        ? Array.from(includeRoot.childNodes)
        : includeRoot
          ? [includeRoot]
          : [];
    const importedNodes = sourceNodes.map((node) => doc.importNode(node, true));
    includeElement.replaceWith(...importedNodes);
  }
};

const expandXacroDocument = async (
  file: File,
  fileKey: string,
  inheritedVariables: Map<string, string>,
  context: XacroContext
): Promise<Document> => {
  const identity = fileIdentityKey(file, fileKey);
  if (context.visited.has(identity)) {
    context.warnings.push(`[Xacro] Cyclic include at "${fileKey || file.name}" was skipped.`);
    return createEmptyXacroDocument();
  }
  context.visited.add(identity);

  const doc = await parseXml(file, "expanding xacro");
  const variables = inheritedVariables;
  await expandIncludes(doc, fileKey, variables, context);
  if (doc.documentElement) processXacroDefinitions(doc.documentElement, variables, context);

  context.visited.delete(identity);
  return doc;
};

const collectXacroIncludeDependenciesFromDocument = async (
  file: File,
  fileKey: string,
  context: XacroContext
): Promise<MjcfUploadDependency[]> => {
  const identity = fileIdentityKey(file, fileKey);
  if (context.visited.has(identity)) return [];
  context.visited.add(identity);

  const doc = await parseXml(file, "checking xacro dependencies for");
  const dependencies: MjcfUploadDependency[] = [];
  const includeElements: Element[] = [];
  const collectIncludes = (element: Element): void => {
    Array.from(element.children).forEach((child) => {
      if (isXacroElement(child, "include")) includeElements.push(child);
      collectIncludes(child);
    });
  };
  if (doc.documentElement) collectIncludes(doc.documentElement);

  for (const includeElement of includeElements) {
    const rawIncludePath =
      includeElement.getAttribute("filename") || includeElement.getAttribute("file") || "";
    const includePath = substituteText(rawIncludePath, new Map(), context);
    const dependencyPath = normalizeUploadPath(includePath || rawIncludePath);
    if (!dependencyPath) continue;
    dependencies.push({ kind: "include", path: dependencyPath, source: "xacro" });

    const resolved = resolveFileFromMap(dependencyPath, fileKey, context.fileMap);
    if (!resolved) continue;
    dependencies.push(
      ...(await collectXacroIncludeDependenciesFromDocument(resolved.file, resolved.key, context))
    );
  }

  context.visited.delete(identity);
  return dependencies;
};

export async function collectXacroIncludeDependenciesFromFileTree(
  rootFile: File,
  fileMap: FileMap
): Promise<MjcfUploadDependency[]> {
  const packageRoots = await buildPackageRoots(fileMap);
  const rootKey = getFileMapKeyForFile(rootFile, fileMap);
  const context: XacroContext = {
    fileMap,
    packageRoots,
    macros: new Map(),
    macroStack: new Set(),
    warnings: [],
    referencedFiles: new Set<string>(),
    visited: new Set<string>(),
  };
  const dependencies = await collectXacroIncludeDependenciesFromDocument(
    rootFile,
    rootKey,
    context
  );
  const seen = new Set<string>();
  return dependencies.filter((dependency) => {
    const key = `${dependency.kind}:${dependency.path.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function expandXacroFile(
  rootFile: File,
  fileMap: FileMap
): Promise<XacroExpansionResult> {
  const packageRoots = await buildPackageRoots(fileMap);
  const rootKey = getFileMapKeyForFile(rootFile, fileMap);
  const context: XacroContext = {
    fileMap,
    packageRoots,
    macros: new Map(),
    macroStack: new Set(),
    warnings: [],
    referencedFiles: new Set<string>(),
    visited: new Set<string>(),
  };
  const doc = await expandXacroDocument(rootFile, rootKey, new Map(), context);
  const xml = new XMLSerializer().serializeToString(doc);
  const urdfFileName = rootFile.name.replace(/(?:\.urdf)?\.xacro$/i, ".urdf");
  return {
    urdfFile: new File([xml], urdfFileName, { type: "application/xml" }),
    warnings: Array.from(new Set(context.warnings)),
    referencedFiles: Array.from(context.referencedFiles),
  };
}
